import React from "react";
import {
  ProxyErrorReport,
  ProxyState,
} from "@intelligems/ig-embedded-preview/messages";
import { PluginState } from "@intelligems/ig-types/schemas/plugin/plugin/embedded";
import { Dispatcher } from "@src/components/ReverseProxy/Dispatcher";

export type ReverseProxyEventMap = {
  initialize: void;
  proxyState: ProxyState;
  pluginState: PluginState;
  navigate: void;
  navigateCommit: void;
};

export interface ReverseProxyURLOptions {
  url?: string | null;
}

export class ReverseProxyController<
  Events extends ReverseProxyEventMap = ReverseProxyEventMap,
  URLOptions extends ReverseProxyURLOptions = ReverseProxyURLOptions,
> extends Dispatcher<Events> {
  protected ref: React.RefObject<HTMLIFrameElement | null> | undefined;
  protected forceUpdate: (() => void) | undefined;
  protected initialized = false;
  protected targetUrl: URL | null | undefined;
  protected proxyUrl: URL | undefined;

  // to support both removing the event listener while retaining this context
  private handleMessageEvent = (ev: MessageEvent<unknown>) =>
    this.onMessageEvent(ev);

  public init(options: {
    ref: React.RefObject<HTMLIFrameElement | null>;
    forceUpdate: () => void;
  }) {
    this.ref = options.ref;
    this.forceUpdate = options.forceUpdate;
    window.addEventListener("message", this.handleMessageEvent);
  }

  public destroy() {
    super.destroy();
    window.removeEventListener("message", this.handleMessageEvent);
  }

  public onLoad() {}

  public onError() {}

  public getSrc(options: URLOptions) {
    const resolvedURL = this.resolveURL(options);
    if (!this.initialized && resolvedURL) {
      this.initialized = true;
      this.onInitialized();
    }
    return resolvedURL?.href;
  }

  protected resolveURL(options: URLOptions) {
    this.targetUrl = this.createTargetUrl(options);
    if (!this.targetUrl) {
      return undefined;
    }

    this.proxyUrl = this.createProxyUrl(options);
    // The proxy resolves the page from `__url__` alone; the path is mirrored for
    // in-page code that reads `location.pathname` (e.g. the PDP handle).
    this.proxyUrl.pathname = this.targetUrl.pathname;
    this.proxyUrl.searchParams.set("__url__", this.targetUrl.href);
    return this.proxyUrl;
  }

  protected onMessageEvent(ev: MessageEvent<unknown>) {
    if (ev.source) {
      if (ev.source !== this.ref?.current?.contentWindow) {
        return;
      }
    } else {
      if (ev.origin !== this.proxyUrl?.origin) {
        return;
      }
    }
    const message = this.parseMessage(ev);
    if (message) {
      this.onMessage(message.type, message.payload);
    }
  }

  protected onMessage(type: string, payload: unknown) {
    switch (type) {
      case "ig:ready":
        this.onProxyState(payload as ProxyState);
        break;
      case "ig:beforeunload":
        this.onNavigate();
        break;
      case "ig:pagehide":
        this.onNavigateCommit();
        break;
      case "ig-pageLoad":
        this.onPluginState(payload as PluginState);
        break;
      case "ig:error":
        this.onProxyError(payload as ProxyErrorReport);
        break;
    }
  }

  protected onInitialized() {
    this.emit("initialize", undefined);
  }

  protected onProxyState(state: ProxyState) {
    this.emit("proxyState", state);
  }

  protected onNavigate() {
    this.emit("navigate", undefined);
  }

  protected onNavigateCommit() {
    this.emit("navigateCommit", undefined);
  }

  protected onPluginState(state: PluginState) {
    this.emit("pluginState", state);
  }

  protected onProxyError(_report: ProxyErrorReport) {
    this.onError();
  }

  protected parseMessage(ev: MessageEvent<unknown>) {
    if (!ev.data) {
      return undefined;
    }
    if (typeof ev.data !== "object") {
      return undefined;
    }

    const type =
      "type" in ev.data && typeof ev.data.type === "string"
        ? ev.data.type
        : undefined;
    const payload: unknown = "payload" in ev.data ? ev.data.payload : undefined;
    return type ? { type, payload } : undefined;
  }

  protected createTargetUrl(options: URLOptions) {
    if (!options.url) {
      return undefined;
    }

    return new URL(options.url);
  }

  /**
   * Build the proxy iframe `src` for a target URL. By default we route to a
   * visual-builder cookie-isolation subdomain — `<target_underscored>.<main>` —
   * so each target gets its own browser cookie jar and concurrent embeds
   * of different storefronts don't trample each other on the shared main
   * host. The encoding mirrors what the backend already does for
   * cross-origin iframe redirects in `proxyURL.ts`, so the same target
   * consistently resolves to the same proxy host across both directions.
   *
   * Subclasses can override — `ShopPreviewController` emits a
   * `preview-<N>.<main>` host for theme/template preview iframes, which
   * the reverse proxy treats as non-interactive (plugin dropped, popups
   * blocked, cart stubbed).
   */
  protected createProxyUrl(options: URLOptions) {
    const url = new URL(import.meta.env.VITE_REVERSE_PROXY_URL as string);
    if (options.url) {
      const target = new URL(options.url);
      url.hostname = `${target.hostname.replaceAll(".", "_")}.${url.hostname}`;
    }
    return url;
  }
}
